import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, forkJoin, map, of, switchMap } from 'rxjs';

import { getApiErrorMessage } from '../../../core/utils/api-error.util';
import { HelpSupportTicketsService } from '../services/help-support-tickets.service';
import { HelpSupportTicketsActions } from './help-support-tickets.actions';

@Injectable()
export class HelpSupportTicketsEffects {
  private readonly actions$ = inject(Actions);
  private readonly service = inject(HelpSupportTicketsService);

  readonly loadList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HelpSupportTicketsActions.loadList),
      switchMap(({ query }) =>
        forkJoin({
          list: this.service.listTickets(query),
          stats: this.service.getStats(),
        }).pipe(
          map(({ list, stats }) =>
            HelpSupportTicketsActions.loadListSuccess({
              rows: list.items,
              total: list.pagination.total,
              page: list.pagination.page,
              pageSize: list.pagination.pageSize,
              stats,
            }),
          ),
          catchError((error: unknown) =>
            of(
              HelpSupportTicketsActions.loadListFailure({
                error: getApiErrorMessage(error, 'Unable to load support tickets'),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly loadCategories$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HelpSupportTicketsActions.loadCategories),
      switchMap(() =>
        this.service.listCategories().pipe(
          map((categories) => HelpSupportTicketsActions.loadCategoriesSuccess({ categories })),
          catchError((error: unknown) =>
            of(
              HelpSupportTicketsActions.loadCategoriesFailure({
                error: getApiErrorMessage(error, 'Unable to load support categories'),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly loadDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HelpSupportTicketsActions.loadDetail),
      switchMap(({ ticketId }) =>
        this.service.getTicket(ticketId).pipe(
          map((ticket) => HelpSupportTicketsActions.loadDetailSuccess({ ticket })),
          catchError((error: unknown) =>
            of(
              HelpSupportTicketsActions.loadDetailFailure({
                error: getApiErrorMessage(error, 'Unable to load ticket details'),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly createTicket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HelpSupportTicketsActions.createTicket),
      switchMap(({ payload }) =>
        this.service.createTicket(payload).pipe(
          map((ticket) =>
            HelpSupportTicketsActions.createTicketSuccess({
              ticket,
              message: `Ticket ${ticket.ticketId} created successfully`,
            }),
          ),
          catchError((error: unknown) =>
            of(
              HelpSupportTicketsActions.createTicketFailure({
                error: getApiErrorMessage(error, 'Unable to create support ticket'),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly createMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HelpSupportTicketsActions.createMessage),
      switchMap(({ ticketId, payload }) =>
        this.service.createMessage(ticketId, payload).pipe(
          switchMap(() => this.service.getTicket(ticketId)),
          map((ticket) =>
            HelpSupportTicketsActions.createMessageSuccess({
              ticket,
              toast: 'Reply added successfully',
            }),
          ),
          catchError((error: unknown) =>
            of(
              HelpSupportTicketsActions.createMessageFailure({
                error: getApiErrorMessage(error, 'Unable to add reply'),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly createInternalNote$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HelpSupportTicketsActions.createInternalNote),
      switchMap(({ ticketId, payload }) =>
        this.service.createInternalNote(ticketId, payload).pipe(
          map((note) =>
            HelpSupportTicketsActions.createInternalNoteSuccess({
              note,
              toast: 'Internal note added successfully',
            }),
          ),
          catchError((error: unknown) =>
            of(
              HelpSupportTicketsActions.createInternalNoteFailure({
                error: getApiErrorMessage(error, 'Unable to add internal note'),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly changeAssignee$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HelpSupportTicketsActions.changeAssignee),
      switchMap(({ ticketId, payload }) =>
        this.service.changeAssignee(ticketId, payload).pipe(
          map((ticket) =>
            HelpSupportTicketsActions.changeAssigneeSuccess({
              ticket,
              toast: 'Assignee updated successfully',
            }),
          ),
          catchError((error: unknown) =>
            of(
              HelpSupportTicketsActions.changeAssigneeFailure({
                error: getApiErrorMessage(error, 'Unable to change assignee'),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly changeCategory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HelpSupportTicketsActions.changeCategory),
      switchMap(({ ticketId, payload }) =>
        this.service.changeCategory(ticketId, payload).pipe(
          map((ticket) =>
            HelpSupportTicketsActions.changeCategorySuccess({
              ticket,
              toast: 'Category updated successfully',
            }),
          ),
          catchError((error: unknown) =>
            of(
              HelpSupportTicketsActions.changeCategoryFailure({
                error: getApiErrorMessage(error, 'Unable to change category'),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly changePriority$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HelpSupportTicketsActions.changePriority),
      switchMap(({ ticketId, payload }) =>
        this.service.changePriority(ticketId, payload).pipe(
          map((ticket) =>
            HelpSupportTicketsActions.changePrioritySuccess({
              ticket,
              toast: 'Priority updated successfully',
            }),
          ),
          catchError((error: unknown) =>
            of(
              HelpSupportTicketsActions.changePriorityFailure({
                error: getApiErrorMessage(error, 'Unable to change priority'),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly assignToMe$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HelpSupportTicketsActions.assignToMe),
      switchMap(({ ticketId }) =>
        this.service.assignToMe(ticketId).pipe(
          map((ticket) =>
            HelpSupportTicketsActions.assignToMeSuccess({
              ticket,
              toast: 'Ticket assigned to you',
            }),
          ),
          catchError((error: unknown) =>
            of(
              HelpSupportTicketsActions.assignToMeFailure({
                error: getApiErrorMessage(error, 'Unable to assign ticket to you'),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly closeTicket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HelpSupportTicketsActions.closeTicket),
      switchMap(({ ticketId, payload }) =>
        this.service.closeTicket(ticketId, payload).pipe(
          map((ticket) =>
            HelpSupportTicketsActions.closeTicketSuccess({
              ticket,
              toast: 'Ticket closed successfully',
            }),
          ),
          catchError((error: unknown) =>
            of(
              HelpSupportTicketsActions.closeTicketFailure({
                error: getApiErrorMessage(error, 'Unable to close ticket'),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly reopenTicket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HelpSupportTicketsActions.reopenTicket),
      switchMap(({ ticketId }) =>
        this.service.reopenTicket(ticketId).pipe(
          map((ticket) =>
            HelpSupportTicketsActions.reopenTicketSuccess({
              ticket,
              toast: 'Ticket reopened successfully',
            }),
          ),
          catchError((error: unknown) =>
            of(
              HelpSupportTicketsActions.reopenTicketFailure({
                error: getApiErrorMessage(error, 'Unable to reopen ticket'),
              }),
            ),
          ),
        ),
      ),
    ),
  );
}
